import {
  Avatar,
  Footer,
  Header,
  HeaderOptionItem,
} from "@appkit4/react-components";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import toast from "./components/DismissibleToast";
import { GroupAccessRoute } from "./components/GroupAccessRoute";
import PeriodFinalisedBanner from "./components/PeriodFinalisedBanner";
import {
  allPages as appPages,
  periodAccessPages,
  ProgressNav,
} from "./components/ProgressNav";
import { refreshReadonly } from "./components/ReadonlyAwareInputs";
import SupportPanel from "./components/SupportPanel";
import AcceptableUsePolicyStatement from "./pages/acceptableUsePolicyStatement";
import AlreadyOpen from "./pages/AlreadyOpen";
import AuthCallback from "./pages/AuthCallback";
import CompanyAP from "./pages/company-ap/CompanyAP";
import CookiesStatement from "./pages/cookiesStatement";
import EditBFD from "./pages/editBFD/EditBFD";
import EditCFD from "./pages/editCFD/EditCFD";
import EntityInput from "./pages/entity-input/EntityInput";
import Unauthorised from "./pages/ErrorHandler";
import GroupCompanies from "./pages/group-companies/GroupCompanies";
import GroupData from "./pages/group-data/POAGroupData";
import GroupConfig from "./pages/groupConfig/groupConfig";
import GroupPOA from "./pages/GroupPOA/GroupPOA";
import Welcome from "./pages/home/Welcome";
import IdleWarnModal from "./pages/IdleWarnModal";
import Logout from "./pages/logout";
import NewGroup from "./pages/new-group/NewGroup";
import Outputs from "./pages/outputs/Outputs";
import PrivacyStatement from "./pages/privacyStatement";
import { ProtectedRoute } from "./pages/ProtectedRoute";
import ReadonlyBanner from "./pages/ReadonlyBanner";
import ReportingCompany from "./pages/reporting-company/ReportingCompany";
import ReviewCalc1 from "./pages/ReviewCalc1";
import ReviewCalc2 from "./pages/ReviewCalc2";
import ReviewCalc3 from "./pages/ReviewCalc3";
import ReviewCalc4 from "./pages/ReviewCalc4";
import Summary from "./pages/Summary";
import UserAccess from "./pages/user-management/ManageUserAccess";

import authService from "./services/auth/auth.service";
import { AuthContext } from "./services/AuthProvider";
import {
  getGroupID,
  getGroupName,
  getLastNavigatedLocation,
  getUserIsReadonly,
  hydrateGroupDataState,
  resetGroupData,
  setGroupData,
  setLastNavigatedLocation,
  setPoaIndex,
} from "./services/GroupContext";
import sqlService from "./services/sqldatabase/sqldatabase.service";
import { setCachedLocation } from "./store/slice/usageInfoSlice";
import { store } from "./store/store";
import SingleTabEnforcer from "./utils/SingleTabEnforcer";

function App() {
  const [selectedPOA, setSelectedPOA] = useState({});
  const [idleWarningVisible, setIdleWarningVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const initials = auth?.getInitials();

  const [groupNameState, setGroupNameState] = useState(null);
  const [isGroupDataReady, setIsGroupDataReady] = useState(false);

  const [isGroupAccessPage, setIsGroupAccessPage] = useState(false);
  const [isPeriodAccessPage, setIsPeriodAccessPage] = useState(false);
  const [supportPanelVisible, setSupportPanelVisible] = useState(false);
  const [supportIconVisible, setSupportIconVisible] = useState(false);

  const user = useSelector((state) => state.usageInfo.user);
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);
  const tenantId = useSelector((state) => state.usageInfo.tenantID);

  const navtohome = async () => {
    const groupName = getGroupName();
    if (groupName) {
      auth?.setReadonly(false);
      try {
        await sqlService?.postRemoveGroupLock(getGroupID());
      } catch (error) {
        toast.error(
          "Error Removing Group Lock on navigating to home for group: ",
          groupName,
        );
        console.log(
          `error removing group lock on nav to home from group: ${groupName}`,
          error,
        );
      }
    }

    resetGroupData();
    setLastNavigatedLocation("/");

    const currentPath = window?.location?.pathname;
    if (currentPath === "/") {
      window?.location?.reload();
    } else {
      navigate("/");
    }
  };

  const applyGroupLockAndNavigate = async (url) => {
    const promise = sqlService.postLockGroup(tenantId, getGroupID());
    await toast.promise(promise, {
      loading: "Locking Group...",
      success: (response) => {
        if (response.status === 200) {
          navigate(url);
          return "Group Locked";
        } else {
          throw new Error(
            `Error Locking Group: ${response.err ?? response.error ?? ""}`,
          );
        }
      },
      error: (err) => `Error locking group: ${err.error ?? err.message ?? ""}`,
    });
  };

  useEffect(() => {
    let isMounted = true;

    hydrateGroupDataState().finally(() => {
      if (isMounted) {
        setIsGroupDataReady(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isGroupDataReady) {
      return;
    }

    const newLocation = location.pathname.toLocaleLowerCase();
    const lastNavigatedLocation = getLastNavigatedLocation();

    if (
      appPages.includes(newLocation) &&
      newLocation !== lastNavigatedLocation
    ) {
      setLastNavigatedLocation(location.pathname);
    }

    if (newLocation === "/") {
      if (lastNavigatedLocation !== "/") {
        const reOpenAsReadonly = getUserIsReadonly();
        const tenantId = store.getState().usageInfo.tenantID;
        if (tenantId) {
          const refreshPromise = sqlService
            ?.getGroupData(tenantId, getGroupID(), reOpenAsReadonly)
            .then((res) => {
              const { data } = res;

              const lastRequest = store.getState().usageInfo.lastPostRequest;
              const responseTimestamp = data?.group_data?.timestamp;

              if (
                !dayjs(lastRequest).isSame(dayjs(responseTimestamp), "second")
              ) {
                // If updates were posted while the user was logged off, reset cached group data and navigate to Group PoA.
                setGroupData(data?.group_data);
                dispatch(setCachedLocation("/"));
                toast(
                  `While you were logged out, updates were posted to the Group [${getGroupName()}]. \n You have been automatically navigated to the Group PoA page to review the latest changes.`,
                  { icon: "⚠️" },
                );
                applyGroupLockAndNavigate("/app/GroupPOA");
              } else if (reOpenAsReadonly) {
                setGroupData(data?.group_data);
                applyGroupLockAndNavigate(lastNavigatedLocation);
              } else {
                // check lock to reopen as non-readonly
                if (!data?.readonly?.value) {
                  applyGroupLockAndNavigate(lastNavigatedLocation);
                } else {
                  toast.error(
                    `Failed to re-acquire group lock, it is currently being edited by ${data?.readonly?.user}`,
                  );

                  setGroupData({});
                  setPoaIndex(-1);
                  setLastNavigatedLocation("/");
                  auth.setReadonly(false);
                }
              }
            });

          toast.promise(refreshPromise, {
            loading: "Attempting to re-open group",
            success: "Group reopened",
          });
        }
      } else {
        setGroupData({});
        setPoaIndex(-1);
        setLastNavigatedLocation("/");
        auth.setReadonly(false);
      }
    }

    setGroupNameState(getGroupName());

    setIsGroupAccessPage(appPages.includes(location.pathname.toLowerCase()));
    setIsPeriodAccessPage(
      periodAccessPages.includes(location.pathname.toLowerCase()),
    );
  }, [isGroupDataReady, location]);

  refreshReadonly();

  const logout = async () => {
    // end the session and revoke the token
    await authService?.logout();
  };

  if (!isGroupDataReady) {
    return null;
  }

  return (
    <SingleTabEnforcer>
      <div className="container">
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 3000,
          }}
        />
        <div className="row">
          <Header
            type="solid"
            onClickLogo={() => {
              navtohome();
            }}
            titleTemplate={() => "Corporate Interest Restriction Analyser"}
            optionsTemplate={() => {
              return (
                <>
                  <HeaderOptionItem
                    iconName="home-outline"
                    label="Home"
                    onClick={() => {
                      navtohome();
                    }}
                  ></HeaderOptionItem>
                  {supportIconVisible && (
                    <HeaderOptionItem
                      iconName="help-question-outline"
                      label="Support"
                      onClick={() => {
                        setSupportPanelVisible(true);
                      }}
                    ></HeaderOptionItem>
                  )}
                  {auth?.hasUser() && (
                    <HeaderOptionItem
                      iconName="logout-outline"
                      label="Logout"
                      onClick={logout}
                    ></HeaderOptionItem>
                  )}
                </>
              );
            }}
            userTemplate={() => (
              <div data-theme="orange">
                <Avatar label={initials} disabled={true}></Avatar>
              </div>
            )}
          />
        </div>

        <div className="ap-container">
          {" "}
          {/* add max width */}
          <IdleWarnModal></IdleWarnModal>
          <div className="row">
            <div className="mt-md-1">
              {groupNameState && (
                <h1 className="title2">
                  {!user && "Last Opened Group: "}
                  {groupNameState}
                </h1>
              )}
              {isGroupAccessPage && <ProgressNav />}
              {isGroupAccessPage && <ReadonlyBanner />}
              {isPeriodAccessPage && <PeriodFinalisedBanner />}
              <Routes>
                <Route
                  path=""
                  element={
                    <ProtectedRoute>
                      <Welcome setSupportIconVisible={setSupportIconVisible} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/new"
                  element={
                    <ProtectedRoute>
                      <NewGroup />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/useraccess"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <UserAccess />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/groupconfig"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <GroupConfig />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/grouppoa"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <GroupPOA />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/groupcompanies"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <GroupCompanies />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/companyap"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <CompanyAP />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/reportingcompany"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <ReportingCompany />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/groupdata"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <GroupData />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/reviewcalc1"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <ReviewCalc1 />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/reviewcalc2"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <ReviewCalc2 />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/reviewcalc3"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <ReviewCalc3 />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/reviewcalc4"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <ReviewCalc4 />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/outputs"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <Outputs />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/summary"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <Summary />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/entityinput"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <EntityInput />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/editbfd"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <EditBFD />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/editcfd"
                  element={
                    <ProtectedRoute>
                      <GroupAccessRoute>
                        <EditCFD />
                      </GroupAccessRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="auth/callback" element={<AuthCallback />} />
                <Route path="app/logout" element={<Logout />} />
                <Route path="app/unauthorised" element={<Unauthorised />} />
                <Route
                  path="app/cookiesstatement"
                  element={<CookiesStatement />}
                />
                <Route
                  path="app/acceptableusepolicy"
                  element={<AcceptableUsePolicyStatement />}
                />
                <Route
                  path="app/privacystatement"
                  element={<PrivacyStatement />}
                />
                <Route path="app/already-open" element={<AlreadyOpen />} />
              </Routes>
            </div>
            <SupportPanel
              visible={supportPanelVisible}
              setVisible={setSupportPanelVisible}
            ></SupportPanel>
            <Footer
              content={`© ${new Date().getFullYear()} PwC. All rights reserved. PwC refers to the PwC network and/or one or more of its member firms, each of which is a separate legal entity. Please see <a class='ap-link' href='https://www.pwc.com/structure'>www.pwc.com/structure</a> for further details.`}
              type="links"
              links={[
                {
                  name: "Privacy Policy",
                  href: "https://www.pwc.co.uk/who-we-are/privacy-statement.html",
                  target: "_self",
                },
                ...(isClientFacing
                  ? [
                      {
                        name: "Acceptable Use Policy",
                        href:
                          window?.location?.origin + "/app/acceptableusepolicy",
                        target: "_self",
                      },
                    ]
                  : []),
                {
                  name: "Cookie Notice",
                  href: window?.location?.origin + "/app/cookiesStatement",
                  target: "_self",
                },
              ]}
              className="mb-md-3 mt-md-3"
            ></Footer>
          </div>
        </div>
      </div>
    </SingleTabEnforcer>
  );
}

export default App;
